import React, { useState } from 'react';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';

const RecurrenceMoveModal = ({ isOpen, onClose, onConfirm, appointment, actionType = 'move' }) => {
  const [selectedScope, setSelectedScope] = useState(null);
  const [endDate, setEndDate] = useState('');

  const handleConfirm = (scope) => {
    if (scope === 'period' && !endDate) {
      return; // Ne pas confirmer si la date de fin n'est pas définie
    }
    
    const data = {
      scope: scope,
      endDate: scope === 'period' ? endDate : null
    };
    
    onConfirm(data);
  };

  const handleScopeSelect = (scope) => {
    setSelectedScope(scope);
  };

  const resetForm = () => {
    setSelectedScope(null);
    setEndDate('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    return `${hours}h${minutes}`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <EnhancedModal onClose={handleClose} size="md">
      <ModalHeader>
        {actionType === 'move' ? 'Déplacer' : 'Modifier'} un rendez-vous récurrent
      </ModalHeader>
      <ModalBody>
        <div className="space-y-4 md:space-y-6">
          {/* Informations du rendez-vous */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 md:p-4">
            <div className="flex items-start space-x-2 md:space-x-3">
              <div className="flex-shrink-0">
                <Calendar className="text-blue-500 dark:text-blue-400" size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1 md:mb-2 text-sm md:text-base">
                  Rendez-vous récurrent
                </h4>
                <div className="space-y-1 text-xs md:text-sm text-blue-800 dark:text-blue-200">
                  <div className="flex items-center space-x-1 md:space-x-2">
                    <Clock size={12} />
                    <span className="truncate">
                      {formatTime(appointment?.startTime)} - {formatTime(appointment?.endTime)}
                    </span>
                  </div>
                  {appointment?.date && (
                    <div className="truncate">
                      {formatDate(appointment.date)}
                    </div>
                  )}
                  {appointment?.details && (
                    <div className="text-blue-700 dark:text-blue-300 truncate">
                      {appointment.details}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Message d'alerte */}
          <div className="flex items-start space-x-2 md:space-x-3 p-3 md:p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
            <AlertCircle className="text-yellow-500 dark:text-yellow-400 flex-shrink-0 mt-0.5" size={16} />
            <div className="text-xs md:text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium mb-1">Ce rendez-vous fait partie d'une série récurrente</p>
              <p>Choisissez quelles occurrences vous souhaitez déplacer :</p>
            </div>
          </div>

          {/* Options de portée */}
          <div className="space-y-2 md:space-y-3">
            <button
              onClick={() => handleConfirm('this')}
              className="w-full p-3 md:p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-300 dark:hover:border-blue-500 transition-colors"
            >
              <div className="font-medium text-gray-900 dark:text-gray-100 text-sm md:text-base">Ce rendez-vous</div>
              <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                {actionType === 'move' ? 'Déplacer' : 'Modifier'} uniquement ce rendez-vous spécifique
              </div>
            </button>

            <button
              onClick={() => handleConfirm('future')}
              className="w-full p-3 md:p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-300 dark:hover:border-blue-500 transition-colors"
            >
              <div className="font-medium text-gray-900 dark:text-gray-100 text-sm md:text-base">Ce rendez-vous et les suivants</div>
              <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                {actionType === 'move' ? 'Déplacer' : 'Modifier'} ce rendez-vous et tous les rendez-vous à partir de celui-ci
              </div>
            </button>

            {/* Nouvelle option : Pour une période définie */}
            <div className={`p-3 md:p-4 border rounded-lg transition-colors ${
              selectedScope === 'period' 
                ? 'border-blue-300 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30' 
                : 'border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-300 dark:hover:border-blue-500'
            }`}>
              <button
                onClick={() => handleScopeSelect('period')}
                className="w-full text-left"
              >
                <div className="font-medium text-gray-900 dark:text-gray-100 text-sm md:text-base">Pour une période définie</div>
                <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {actionType === 'move' ? 'Déplacer' : 'Modifier'} ce rendez-vous et les suivants jusqu'à une date spécifique
                </div>
              </button>
              
              {/* Sélecteur de date de fin - visible uniquement quand l'option est sélectionnée */}
              {selectedScope === 'period' && (
                <div className="mt-3 md:mt-4 p-3 md:p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Date de fin du remplacement
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={appointment?.date ? new Date(appointment.date).toISOString().split('T')[0] : ''}
                    className="w-full p-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Après cette date, les rendez-vous reprendront leur intervenant original
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <ModalButton variant="secondary" onClick={handleClose}>
          Annuler
        </ModalButton>
        {selectedScope === 'period' && endDate && (
          <ModalButton 
            variant="primary" 
            onClick={() => handleConfirm('period')}
          >
            Confirmer le remplacement temporaire
          </ModalButton>
        )}
      </ModalFooter>
    </EnhancedModal>
  );
};

export default RecurrenceMoveModal;
